import React, { useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import api from "../api";
import { Av } from "./Avatar";

// Full reporting hierarchy for one employee: the whole chain above them
// (manager → manager's manager → … → top) and their team below, where every
// person with reports can be expanded in place to show *their* reports, as
// many levels deep as the chain goes. Reports are fetched lazily on expand.

const MAX_UP = 12;

const PersonRow = ({ p, onOpen, right, tone = "bg-bg2" }) => (
  <button
    onClick={() => onOpen(p)}
    className={`flex-1 min-w-0 flex items-center gap-3 p-3 ${tone} rounded-xl border-0 cursor-pointer text-left hover:bg-bg3 transition-colors`}
  >
    <Av nm={p.name} sz={38} src={p.photo} />
    <div className="min-w-0 flex-1">
      <div className="font-bold text-t0 text-[14px] truncate">{p.name}</div>
      <div className="text-[12px] text-t3 truncate">{p.designation || "—"}</div>
      {p.email && <div className="text-[11px] text-t3 truncate">{p.email}</div>}
    </div>
    {right}
  </button>
);

const DownNode = ({ node, onOpen, depth = 0 }) => {
  const [open, setOpen] = useState(false);
  const [kids, setKids] = useState(null);
  const [loading, setLoading] = useState(false);
  const has = (node.reportsCount || 0) > 0;

  const toggle = async () => {
    if (!has) return;
    if (!open && kids === null) {
      setLoading(true);
      try { setKids((await api.get("/api/employees/" + node.id + "/team-node"))?.directReports || []); }
      catch { setKids([]); }
      setLoading(false);
    }
    setOpen(v => !v);
  };

  return (
    <div>
      <div className="flex items-stretch gap-[6px]">
        {has ? (
          <button
            onClick={toggle}
            title={open ? "Collapse" : `Show ${node.reportsCount} direct report${node.reportsCount > 1 ? "s" : ""}`}
            className="flex-shrink-0 w-[44px] flex flex-col items-center justify-center gap-[2px] rounded-xl border-0 bg-pg text-p cursor-pointer"
          >
            <ChevronRight size={14} className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
            <span className="text-[11px] font-bold">{node.reportsCount}</span>
          </button>
        ) : <div className="flex-shrink-0 w-[44px]" />}
        <PersonRow p={node} onOpen={onOpen} />
      </div>
      {open && (
        <div className="ml-[22px] pl-[14px] mt-[6px] flex flex-col gap-[6px] border-l-2 border-b1">
          {loading && <div className="text-[12px] text-t3 py-1">Loading…</div>}
          {kids && kids.map(c => <DownNode key={c.id} node={c} onOpen={onOpen} depth={depth + 1} />)}
        </div>
      )}
    </div>
  );
};

export const EmployeeTeamTree = ({ teamNode, empName, onOpen }) => {
  const [upline, setUpline] = useState(null); // top-most first; null = loading

  // Walk up the chain starting from the direct manager.
  useEffect(() => {
    let dead = false;
    setUpline(null);
    (async () => {
      const chain = [];
      const seen = new Set([teamNode.self?.id].filter(Boolean));
      let m = teamNode.manager;
      while (m && !seen.has(m.id) && chain.length < MAX_UP) {
        seen.add(m.id);
        chain.unshift(m);
        try { m = (await api.get("/api/employees/" + m.id + "/team-node"))?.manager; }
        catch { break; }
      }
      if (!dead) setUpline(chain);
    })();
    return () => { dead = true; };
  }, [teamNode]);

  const reports = teamNode.directReports || [];

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-[12px] text-t3 uppercase mb-[6px]">Reporting line (above)</div>
        {upline === null ? (
          <div className="text-[13px] text-t3 p-3">Loading hierarchy…</div>
        ) : upline.length === 0 ? (
          <div className="text-center p-4 text-t3 text-[14px] bg-bg2 rounded-xl">{empName || "This employee"} has no reporting manager (top of the hierarchy).</div>
        ) : (
          <div className="flex flex-col gap-[6px]">
            {upline.map((p, i) => (
              <div key={p.id} style={{ marginLeft: i * 18 }} className="flex items-center gap-2">
                <PersonRow
                  p={p}
                  onOpen={onOpen}
                  right={<span className="text-[10.5px] font-bold text-t3 bg-bg1 px-[8px] py-[3px] rounded-full flex-shrink-0">{i === upline.length - 1 ? "Reports to" : i === 0 ? "Top" : `Level ${upline.length - i}`}</span>}
                />
              </div>
            ))}
            <div style={{ marginLeft: upline.length * 18 }} className="flex items-center gap-3 p-3 rounded-xl border-2 border-p bg-pg">
              <Av nm={empName || "?"} sz={38} src={teamNode.self?.photo} />
              <div className="font-bold text-t0 text-[14px]">{empName || teamNode.self?.name} <span className="text-p text-[12px] font-semibold">· this employee</span></div>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="text-[12px] text-t3 uppercase mb-[6px]">Team below · Direct Reports ({reports.length})</div>
        {reports.length === 0 ? (
          <div className="text-center p-6 text-t3 text-[14px] bg-bg2 rounded-xl">No one reports to {empName || "this employee"}.</div>
        ) : (
          <div className="flex flex-col gap-[6px]">
            {reports.map(r => <DownNode key={r.id} node={r} onOpen={onOpen} />)}
          </div>
        )}
      </div>
    </div>
  );
};
